import styled from 'styled-components';

import { getBasketPrices } from '@front/common/src/ducks/customer/basket-selectors';
import { useConfig } from '@front/common/src/providers/config-provider';
import type { BasketContent, ProGender, User, WellnessBasketPrices } from '@front/common/src/types/commons';
import { hasPrestations } from '@front/common/src/utils/basket';

import type { CommonBasketProps } from '@front/customer/src/components/basket/types';

import Separator from '@front/web-ui/src/components/separator/separator';

import useBasketSection from '../../../hooks/use-basket-section';
import { PRESTATIONS_PATH, route } from '../../../steps/steps';
import { useAppSelector } from '../../../store';
import { StyledBasketContainer } from '../common/common';
import NoContentYet from '../common/no-content-yet';
import Address from '../common/sections/address/address';
import ProSection from '../common/sections/pros/pro-section';
import Appointments from './sections/appointments/appointments';
import MembershipCard from './sections/membership-card/membership-card';
import PrestationsPrices from './sections/prestations/prestations-prices';

const StyledSeparator = styled(Separator)`
  margin-bottom: 20px;
`;

export type WellnessBasketProps = CommonBasketProps & {
  basketContent: BasketContent;
  proGender?: ProGender | null;
  basketContentDuration: number;
  hasMembership: boolean;
  canSubscribeToMembership: boolean;
  setIsMembershipRequested: (isRequested: boolean) => void;
  openMembershipPaywall: () => void;
  pathname: string;
  user?: User;
  shouldShowMembershipCard: boolean;
  shouldShowPromoCode: boolean;
};

const WellnessBasket = ({
  universe,
  promotion,
  appointment,
  children,
  address,
  proGender,
  proChoices,
  addressDetails,
  setAddressDetails,
  basketContentDuration,
  hasFreeParking,
  toggleHasFreeParking,
  hasMembership,
  canSubscribeToMembership,
  setIsMembershipRequested,
  openMembershipPaywall,
  pathname,
  user,
  basketContent,
  shouldShowMembershipCard,
  shouldShowPromoCode,
}: WellnessBasketProps) => {
  const config = useConfig();
  const prices = useAppSelector((state) => getBasketPrices(state, config)) as WellnessBasketPrices;

  const { isVisible: shouldShowMembershipCardForCurrentScreen } = useBasketSection({
    data: hasPrestations(basketContent),
    sectionLocations: [route(PRESTATIONS_PATH)],
  });

  const shouldRenderMembershipCard = shouldShowMembershipCard && shouldShowMembershipCardForCurrentScreen;

  return prices.basketContentWithPrices?.length > 0 ? (
    <StyledBasketContainer>
      <Address
        address={address}
        addressDetails={addressDetails}
        setAddressDetails={setAddressDetails}
        hasFreeParking={hasFreeParking}
        toggleHasFreeParking={toggleHasFreeParking}
        hasTopSeparator={false}
      />
      <PrestationsPrices
        universe={universe}
        prices={prices}
        promotion={promotion}
        proGender={proGender}
        basketContentDuration={basketContentDuration}
        hasMembership={hasMembership}
        shouldShowPromoCode={shouldShowPromoCode}
      />
      <Appointments universe={universe} appointment={appointment} />
      <ProSection proChoices={proChoices} universeRef={universe.reference} />
      {shouldRenderMembershipCard && (
        <>
          <StyledSeparator />
          <MembershipCard
            isMembershipRequested={hasMembership}
            setIsMembershipRequested={setIsMembershipRequested}
            openMembershipPaywall={openMembershipPaywall}
            pathname={pathname}
            user={user}
            universeReference={universe.reference}
            canSubscribeToMembership={canSubscribeToMembership}
            basketContent={basketContent}
          />
        </>
      )}
      {children}
    </StyledBasketContainer>
  ) : (
    <NoContentYet user={user} universe={universe.reference} />
  );
};

export default WellnessBasket;
